/**
 * The compact progress model for a task: five phases and one status per planned component.
 *
 * Pure projection of ONE `TaskSnapshot` (R3) — the state machine and the work items stay the
 * server's. This only chooses words and shapes: milestones by default, mechanics (the dependency
 * board, per-item views) one click away.
 */
import type { TaskSnapshot, WorkItemView } from "@/services/multi-agent/laneV2/types";
import { restingVerdictOf } from "@/services/multi-agent/laneV2/presentation";
import { buildItemsByKey, itemKey, stepperAnchor } from "./views";

/** Plans with at most this many items get the compact tracker; bigger ones get the summary + Dependencies. */
export const TRACKER_MAX_ITEMS = 5;

export type TrackerPhaseKey = "plan" | "approve" | "build" | "validate" | "ready";
export type TrackerPhaseState = "done" | "active" | "waiting" | "failed" | "stopped" | "todo";

export interface TrackerPhase {
  key: TrackerPhaseKey;
  label: string;
  state: TrackerPhaseState;
}

/** ✓ done · ◐ in progress · ○ waiting · ! needs attention · ⊘ skipped */
export type ComponentState = "done" | "active" | "waiting" | "attention" | "skipped";

export const COMPONENT_GLYPH: Record<ComponentState, string> = {
  done: "✓",
  active: "◐",
  waiting: "○",
  attention: "!",
  skipped: "⊘",
};

export interface TrackerComponent {
  key: string;
  label: string;
  state: ComponentState;
  /** Plain words for the state ("Built", "Checked", "Needs attention") — also the accessible name. */
  detail: string;
}

export interface TrackerModel {
  phases: TrackerPhase[];
  components: TrackerComponent[];
  total: number;
  built: number;
  needsAttention: number;
}

const PHASES: ReadonlyArray<{ key: TrackerPhaseKey; label: string }> = [
  { key: "plan", label: "Plan" },
  { key: "approve", label: "Approve" },
  { key: "build", label: "Build" },
  { key: "validate", label: "Check" },
  { key: "ready", label: "Ready to save" },
];

/** Index of the phase a task state sits in; `applied` is past the last one. */
const STAGE: Record<string, number> = {
  exploring: 0,
  plan_proposed: 1,
  contracting: 2,
  building: 2,
  assembling: 2,
  needs_input: 2,
  needs_budget: 2,
  amendment_proposed: 2,
  changeset_proposed: 3,
  checking: 3,
  validated: 4,
  applying: 4,
  applied: 5,
};

/** States where the active phase is waiting on the reader rather than working. */
const WAITING: ReadonlySet<string> = new Set(["plan_proposed", "needs_input", "needs_budget", "amendment_proposed", "changeset_proposed", "validated"]);

export function trackerPhases(snapshot: Pick<TaskSnapshot, "state" | "transitions"> & Partial<Pick<TaskSnapshot, "validation" | "rest_verdict">>, built: number, total: number): TrackerPhase[] {
  const terminalBad = snapshot.state === "failed" || snapshot.state === "cancelled";
  const anchor = terminalBad ? stepperAnchor(snapshot.state, snapshot.transitions ?? []) : snapshot.state;
  const stage = STAGE[anchor] ?? STAGE[snapshot.state] ?? 2;
  const verdict = restingVerdictOf(snapshot as Pick<TaskSnapshot, "state" | "validation" | "rest_verdict">);
  const checkFailedAtRest = !!verdict?.failed;
  return PHASES.map((p, i) => {
    let state: TrackerPhaseState = i < stage ? "done" : i === stage ? "active" : "todo";
    if (state === "active") {
      if (snapshot.state === "failed") state = "failed";
      else if (snapshot.state === "cancelled") state = "stopped";
      else if (WAITING.has(snapshot.state)) state = "waiting";
    }
    if (checkFailedAtRest && p.key === "validate") state = "failed";
    // A check that did not run / is not understood is not "done": it waits, never reads as a pass.
    else if (verdict && !verdict.passed && p.key === "validate") state = "waiting";
    const label = p.key === "build" && total > 0 ? `Build (${built}/${total})` : p.label;
    return { key: p.key, label, state };
  });
}

/**
 * One component's line. Built/validated words come from the SERVER's per-member
 * `package.members[].validation_status` (current package, matched on type + name) — never from the
 * last check's overall `ok`, which says nothing about which package it ran on. A check failure only
 * counts for an item that is built/validated (a leased item is being rebuilt, whatever an old check said).
 */
function componentOf(planKey: string, label: string, item: WorkItemView | undefined, memberStatus: string | null | undefined): TrackerComponent {
  const mk = (state: ComponentState, detail: string): TrackerComponent => ({ key: planKey, label, state, detail });
  if (!item) return mk("waiting", "Waiting");
  switch (item.status) {
    case "built":
    case "validated":
      if (memberStatus === "validation_failed") return mk("attention", "Needs attention: the check failed");
      if (memberStatus === "validated") return mk("done", "Checked");
      if (memberStatus === "not_validated") return mk("done", "Built · not checked");
      return mk("done", "Built · not checked yet");
    case "leased":
      return mk("active", "Building");
    case "failed":
      return mk("attention", "Needs attention: build failed");
    case "blocked":
      return mk("attention", "Needs attention: blocked");
    case "dropped":
      return mk("skipped", "Skipped");
    default:
      return mk("waiting", "Waiting");
  }
}

export function buildTrackerModel(snapshot: TaskSnapshot): TrackerModel {
  const planItems = snapshot.plan?.items ?? [];
  const byKey = snapshot.build ? buildItemsByKey(snapshot.build.items) : new Map<string, WorkItemView>();
  const members = new Map((snapshot.package?.members ?? []).map((m) => [itemKey(m.metadata_type, m.full_name), m.validation_status]));
  const components = planItems.map((pi) =>
    componentOf(pi.item_id, pi.full_name, byKey.get(itemKey(pi.metadata_type, pi.full_name)), members.get(itemKey(pi.metadata_type, pi.full_name))),
  );
  const built = components.filter((c) => c.state === "done").length;
  const needsAttention = components.filter((c) => c.state === "attention").length;
  return { phases: trackerPhases(snapshot, built, components.length), components, total: components.length, built, needsAttention };
}
